import React, { useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'

import { skipToken } from '@reduxjs/toolkit/query'

import { ElaraErrorBoundary } from '@/components/elara/ElaraErrorBoundary'
import QueryBoundary from '@/components/ui/template/QueryBoundary'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { useConsultationRTCConfig } from '@/features/diagnostic'
import { useDialogState } from '@/hooks/useDialogState'
import useMedconnectDevices from '@/hooks/useMedconnectDevices'
import { useIntro } from '@/hooks/useIntro'
import { DiagnosticAllotment } from '@/layouts/diagnostic/DiagnosticAllotment'
import Sidebar from '@/layouts/diagnostic/Sidebar'
import MobileConsultation from '@/pages/diagnostic/containers/MobileConsultation'
import { AllDiagnostics } from '@/pages/diagnostic/containers/AllInOneDiagnostics'
import EndLocalConsultationDialog from '@/pages/diagnostic/containers/EndLocalConsultationDialog'
import { ElaraRealtimeProvider } from '@/providers/elara/ElaraRealtimeProvider'
import { useElaraRealtimeLicenseEnabled } from '@/providers/elara/hooks/useElaraRealtimeLicenseEnabled'
import { DiagnoticLocalIntro } from '@/utils/onboarding/intro'

import { usePageSetup } from '../model/usePageSetup'
import { useConsultationState } from '../model/useConsultationState'
import { SidebarMenuItem, useSidebarMenu } from '../model/useSidebarMenu'
import { useConsultationLifecycle } from '../model/useConsultationLifecycle'
import { Topbar } from './Topbar'
import { ConsultationLocalContent } from './ConsultationLocalContent'
import { LocalElaraNavigationActions } from './LocalElaraNavigationActions'

export function ConsultationLocalPage() {
  const { consultationId } = useParams()
  const [searchParams] = useSearchParams()
  const defaultTab = searchParams.get('defaultTab')

  const { dialogState, setDialogState, closeDialog } = useDialogState<'end-local-consultation'>('none')

  const hasElaraRealtimeLicense = useElaraRealtimeLicenseEnabled()

  // State hooks
  const { consultation, room } = useConsultationState(consultationId)
  const { isLoading, isError, error } = useGetConsultationQuery(consultationId ?? skipToken)

  // RTC configuration
  const { customDevices, medicalVideoAttendeeInfo, medicalVideoMeetingInfo, peerjsConfig, rpiConfig, stethoscopeAttendeeInfo, stethoscopeMeetingInfo } =
    useConsultationRTCConfig({ streamer: room?.streamer })

  // Sidebar menu
  const { patientMenuItems, equipmentMenuItems, selectedPatientMenuItem, setSelectedPatientMenuItem, setSelectedEquipmentMenuItem, isMobileScreen } =
    useSidebarMenu(consultationId)

  // Lifecycle hooks
  usePageSetup()
  useConsultationLifecycle(consultationId, consultation)
  useMedconnectDevices(true, consultationId ?? '')
  useIntro(DiagnoticLocalIntro, !(!consultation || !room))

  // Handle default tab from URL
  useEffect(() => {
    if (defaultTab) {
      setSelectedEquipmentMenuItem(defaultTab)
    }
  }, [defaultTab, setSelectedEquipmentMenuItem])

  const handleSelectPatientMenuItem = (id: string) => {
    if (id === SidebarMenuItem.DMP) {
      window.open(`/efficience-web?consultationId=${consultationId}`, '_blank')
      return
    }
    if (id === SidebarMenuItem.MSS) {
      window.open(`/efficience-web?consultationId=${consultationId}&mode=mss`, '_blank')
      return
    }

    setSelectedPatientMenuItem(id)
  }

  if (!consultation || !room) {
    return <QueryBoundary isLoading={isLoading} isError={isError} error={error} fallback={null} />
  }

  const elaraWrapper = hasElaraRealtimeLicense
    ? (children: React.ReactNode) => <ElaraRealtimeProvider consultationId={consultationId!}>{children}</ElaraRealtimeProvider>
    : (children: React.ReactNode) => <>{children}</>

  return elaraWrapper(
    <Stack direction="row" sx={{ height: '100%', overflow: 'hidden' }}>
      <Topbar onCloseConsultation={() => setDialogState('end-local-consultation')} />

      {isMobileScreen ? (
        <MobileConsultation
          consultationId={consultationId}
          menuItems={equipmentMenuItems}
          stethoscopeAttendeeInfo={stethoscopeAttendeeInfo}
          stethoscopeMeetingInfo={stethoscopeMeetingInfo}
          audioPeerjsConfig={peerjsConfig}
          videoPeerjsConfig={peerjsConfig}
          rpiConfig={rpiConfig}
          customDevices={customDevices}
          medicalVideoAttendeeInfo={medicalVideoAttendeeInfo}
          medicalVideoMeetingInfo={medicalVideoMeetingInfo}
        />
      ) : (
        <>
          <DiagnosticAllotment>
            <DiagnosticAllotment.Pane>
              <Box sx={{ height: '100%', overflowY: 'auto', pt: '20px', pb: '20px' }}>
                <Container maxWidth="md">
                  <AllDiagnostics consultationId={consultationId} isStreamer={true} />
                </Container>
              </Box>
            </DiagnosticAllotment.Pane>
            <DiagnosticAllotment.Pane>
              <ConsultationLocalContent consultation={consultation} consultationId={consultationId!} selectedMenuItem={selectedPatientMenuItem} />
            </DiagnosticAllotment.Pane>
          </DiagnosticAllotment>
          <Sidebar
            id="patient-sidebar"
            menuItems={patientMenuItems}
            onSelect={handleSelectPatientMenuItem}
            selectedMenuItem={selectedPatientMenuItem}
            anchor="right"
          />
        </>
      )}
      <EndLocalConsultationDialog consultationId={consultationId} onClose={closeDialog} open={dialogState === 'end-local-consultation'} />
      {hasElaraRealtimeLicense && (
        <ElaraErrorBoundary>
          <LocalElaraNavigationActions
            selectedMenuItem={selectedPatientMenuItem}
            onSelectMenuItem={handleSelectPatientMenuItem}
            patientMenuItems={patientMenuItems}
          />
        </ElaraErrorBoundary>
      )}
    </Stack>
  )
}
